/**
 * 悬浮球停靠位的纯几何 + 持久化(无 DOM 依赖,便于单测)。
 *
 * <p>★位置模型 = 边缘吸附 + 竖向比例锚定(iOS AssistiveTouch 模型),**不存裸像素**:裸 px 在
 * resize / 换显示器 / 浏览器缩放后会把球甩出视口或落到不可见处。side 只有左右两档、offsetY 是
 * 轨道比例 → 任意视口下 toPixels 都能算回安全区内。
 */

export type DockSide = "left" | "right";

export interface Dock {
  side: DockSide;
  /** 竖向轨道比例:0 = 贴顶留白处,1 = 贴底留白处。 */
  offsetY: number;
}

/** 球直径(与 AdvisorLauncher 的 h-14/w-14 对齐;>44px 触摸下限)。 */
export const BALL_SIZE = 56;
/** 距视口边缘的安全留白。 */
export const MARGIN = 16;

/**
 * 默认位 = 右侧偏下但**不贴底**。老版 `fixed bottom-6 right-6` 恰好压在表格分页器上——
 * 遮挡不是偶然,是默认位置就错。抬到 0.72 让开箱即不挡,而非「挡了自己拖走」。
 */
export const DEFAULT_DOCK: Dock = { side: "right", offsetY: 0.72 };

/** 设备级偏好:位置是「我这台电脑的习惯」,与账号无关(带身份反而多账号切换时乱跳)。 */
export const DOCK_STORAGE_KEY = "gscq.advisor.launcher.dock.v1";

/** 键盘微调步长(Alt+↑/↓)。 */
const NUDGE_STEP = 0.1;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** 竖向可用行程;视口小到放不下球时为 0(退化成贴顶,不产生负坐标)。 */
function trackHeight(vh: number, size: number, margin: number): number {
  return Math.max(0, vh - margin * 2 - size);
}

/** dock → 视口像素坐标(左上角)。 */
export function toPixels(
  dock: Dock,
  vw: number,
  vh: number,
  size = BALL_SIZE,
  margin = MARGIN
): { x: number; y: number } {
  const track = trackHeight(vh, size, margin);
  // 视口窄到放不下球时 x 退化到 0,保证整球坐标非负(极端窗口不产生负位移)。
  const rightX = Math.max(0, vw - margin - size);
  return {
    x: dock.side === "left" ? Math.min(margin, rightX) : rightX,
    y: Math.min(margin, Math.max(0, vh - size)) + clamp(dock.offsetY, 0, 1) * track,
  };
}

/** 松手落点(左上角像素)→ dock:按**球心**吸最近边,y 反算成轨道比例。 */
export function fromDrop(
  x: number,
  y: number,
  vw: number,
  vh: number,
  size = BALL_SIZE,
  margin = MARGIN
): Dock {
  const track = trackHeight(vh, size, margin);
  return {
    side: x + size / 2 < vw / 2 ? "left" : "right",
    offsetY: track > 0 ? clamp((y - margin) / track, 0, 1) : 0,
  };
}

/** 键盘等价路径(拖动是鼠标专属,必须给键盘同等能力)。 */
export function nudge(dock: Dock, dir: "left" | "right" | "up" | "down"): Dock {
  if (dir === "left" || dir === "right") return { ...dock, side: dir };
  const delta = dir === "up" ? -NUDGE_STEP : NUDGE_STEP;
  return { ...dock, offsetY: clamp(dock.offsetY + delta, 0, 1) };
}

/** 未知来源数据(用户改过 localStorage / 旧版本残留)一律先验再用。 */
function isDock(v: unknown): v is Dock {
  if (typeof v !== "object" || v === null) return false;
  const d = v as Partial<Dock>;
  return (
    (d.side === "left" || d.side === "right") &&
    typeof d.offsetY === "number" &&
    Number.isFinite(d.offsetY) &&
    d.offsetY >= 0 &&
    d.offsetY <= 1
  );
}

/** 读停靠位;任何解析/校验失败都回落默认(悬浮球不该因为脏缓存就消失或飞走)。 */
export function loadDock(): Dock {
  try {
    const raw = localStorage.getItem(DOCK_STORAGE_KEY);
    if (!raw) return DEFAULT_DOCK;
    const parsed: unknown = JSON.parse(raw);
    return isDock(parsed) ? { side: parsed.side, offsetY: parsed.offsetY } : DEFAULT_DOCK;
  } catch {
    return DEFAULT_DOCK;
  }
}

/** 存停靠位;隐私模式/配额满时静默放弃(位置偏好丢失 ≪ 抛错炸掉整个 layout)。 */
export function saveDock(dock: Dock): void {
  try {
    localStorage.setItem(DOCK_STORAGE_KEY, JSON.stringify(dock));
  } catch {
    /* 存不下就算了:下次仍用默认位,不影响本次会话已生效的拖动。 */
  }
}
